import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { particleSpeed } from '../../services/flowSurveyService';
import './FlowMap.css';

/** Seconds to watch a spot so at least one full pump cycle is seen. */
export const WATCH_SECONDS = 60;

/**
 * "Watch for 60 seconds": a countdown that helps observing one full pump cycle
 * before picking the lowest and highest level. Only the end is announced.
 */
export function WatchTimer() {
  const { t } = useTranslation();
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (left === null || left <= 0) return undefined;
    const id = window.setTimeout(() => setLeft((s) => (s === null ? null : s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [left]);

  const running = left !== null && left > 0;
  return (
    <Stack gap="2">
      <Inline gap="3" align="center">
        <Button type="button" variant="secondary" size="sm" onClick={() => setLeft(running ? null : WATCH_SECONDS)} data-testid="watch-timer">
          {running ? t('flow.timer.stop') : t('flow.timer.start', { seconds: WATCH_SECONDS })}
        </Button>
        {running && <span className="flow-map__stopwatch-time" aria-hidden="true">{left}</span>}
      </Inline>
      <LiveRegion>{left === 0 ? <Text size="sm">{t('flow.timer.done')}</Text> : null}</LiveRegion>
    </Stack>
  );
}

export interface ParticleStopwatchProps {
  /** Puts a measured speed (cm/s) into the lowest or highest field. */
  onUse: (end: 'min' | 'max', speed: number) => void;
}

/**
 * Particle test: drop a flake upstream, start when it passes the first mark,
 * stop at the second; distance / time = cm/s. Repeat at peak and trough.
 */
export function ParticleStopwatch({ onUse }: ParticleStopwatchProps) {
  const { t } = useTranslation();
  const [distance, setDistance] = useState('10');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [seconds, setSeconds] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const frame = useRef<number | null>(null);

  // Shows the running time while the stopwatch runs.
  useEffect(() => {
    if (startedAt === null) return undefined;
    const tick = (): void => {
      setNow(performance.now());
      frame.current = window.setTimeout(tick, 100);
    };
    tick();
    return () => {
      if (frame.current !== null) window.clearTimeout(frame.current);
    };
  }, [startedAt]);

  const toggle = (): void => {
    if (startedAt === null) {
      setSeconds(null);
      setStartedAt(performance.now());
    } else {
      setSeconds(Math.round((performance.now() - startedAt) / 100) / 10);
      setStartedAt(null);
    }
  };

  const cm = Number(distance.replace(',', '.'));
  const distanceValid = distance.trim() !== '' && Number.isFinite(cm) && cm > 0 && cm <= 200;
  const speed = seconds !== null && distanceValid ? particleSpeed(cm, seconds) : null;
  const shown = startedAt !== null ? Math.max(0, (now - startedAt) / 1000) : (seconds ?? 0);

  return (
    <Stack gap="3" data-testid="particle-stopwatch">
      <Text color="secondary" size="sm">{t('flow.particle.hint')}</Text>
      <Input
        id="flow-particle-distance"
        type="number"
        inputMode="decimal"
        min={1}
        max={200}
        label={t('flow.particle.distance')}
        value={distance}
        onChange={(e) => setDistance(e.target.value)}
        error={distanceValid ? undefined : String(t('flow.particle.distanceInvalid'))}
      />
      <Inline gap="3" align="center">
        <Button type="button" variant="secondary" onClick={toggle}>
          {startedAt === null ? t('flow.particle.start') : t('flow.particle.stop')}
        </Button>
        <span className="flow-map__stopwatch-time" aria-hidden="true">{shown.toFixed(1)} s</span>
      </Inline>
      {speed !== null && (
        <Stack gap="2">
          <LiveRegion>
            <Text weight="medium" data-testid="particle-speed">{t('flow.particle.result', { seconds, speed })}</Text>
          </LiveRegion>
          <Inline gap="2">
            <Button type="button" size="sm" variant="ghost" onClick={() => onUse('min', speed)}>{t('flow.particle.useMin')}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => onUse('max', speed)}>{t('flow.particle.useMax')}</Button>
          </Inline>
        </Stack>
      )}
    </Stack>
  );
}
